import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { SessionService } from '@src/app/shared/services/session.service';
import { CustomersService, CustomerRequest, RetailersService } from '@src/app/shared/backend/clients';
import { latinCharsetRegexValidator } from '@src/app/shared/validators/latinCharacters.validator';
import { firstValueFrom } from 'rxjs';
import { CountryService } from '@src/app/shared/services/countries-service';
import { GenderService } from '@src/app/shared/services/customerGender.service';
import { emailRegexValidator } from '@src/app/shared/validators/email.validator';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';
import { ComboData } from '@src/app/shared/components/comboData/comboData';

@Component({
    selector: 'app-add-customer',
    templateUrl: './add-customer.component.html',
    styleUrl: './add-customer.component.scss',
    host: {
        class: 'app-add-customer'
    },
    standalone: false
})
export class AddCustomerComponent extends BaseViewComponent implements OnInit {

  currentDate: Date = new Date();
  countries: any[] = [];

  companyLinks = {
    // retailerPrivacy: '',
    // retailerTerms: '',
    brandPrivacy: '',
    brandTerms: ''
  };

  combosData: { [key: string]: ComboData } = {
    'retailers': new ComboData(async (filter) => await firstValueFrom(this.retailersService.apiRetailersFilterComboPost(filter)))
  }

  public customerDetailsFormGroup: FormGroup = this.fb.group({
    firstname: ['', [Validators.required, Validators.maxLength(50)]],
    lastname: ['', [Validators.required, Validators.maxLength(50)]],
    romanisedFirstName: ['', [latinCharsetRegexValidator(), Validators.maxLength(50)]],
    romanisedLastName: ['', [latinCharsetRegexValidator(), Validators.maxLength(50)]],
    email: ['', [Validators.required, emailRegexValidator(), Validators.maxLength(50)]],
    country: [[], [Validators.maxLength(50)]],
    address: ['', [Validators.maxLength(50)]],
    postalCode: ['', [Validators.maxLength(50)]],
    birthDate: [null, [Validators.maxLength(10)]],
    gender: [[], [Validators.maxLength(50)]],
    foreign: ['false'],
    wantEmails: [false],
    acceptedTerms: [false, [Validators.required]],
    acceptedPrivacyPolicy: [false, [Validators.required]],
    retailerId: [[]]
  });

  genders: any = [];

  constructor(
    private router: Router,
    private countryService: CountryService,
    private genderService: GenderService,
    private fb: FormBuilder,
    private customersService: CustomersService,
    private retailersService: RetailersService,
    private sessionService: SessionService
  ) { super(); }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    await this.initCombosData();

    const company = await this.sessionService.getCompanyData();

    this.companyLinks = {
      brandPrivacy: company.privacyPolicyLink ?? '',
      brandTerms: company.termsOfUseLink ?? ''
    };

    this.countries = await this.countryService.getCountries();
    this.genders = this.genderService.getGenderOptions();
  }

  async onLazyLoadComboData(comboType: 'retailers', event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));

    await this.combosData[comboType].getData(search, activePage);
  }

  getComboTotalPages(comboType: 'retailers'): number {
    const totalRecords = this.combosData[comboType]?.totalRecords ?? 0;
    const pageSize = Number(this.combosData[comboType]?.rowsPerPageOption) || 5;

    return Math.max(1, Math.ceil(totalRecords / pageSize));
  }

  private async initCombosData() {
    await this.combosData['retailers'].getData(null, 1);
  }

  async submitForm() {
    if (this.customerDetailsFormGroup.invalid) return;
    const birthDate = this.customerDetailsFormGroup.controls['birthDate'].value;
    const birthDateUtc = birthDate?.toISOString?.();
    const birthDateTimeless = birthDateUtc ? birthDateUtc.split('T')[0] : null;
    const payload: CustomerRequest = {
      firstName: this.customerDetailsFormGroup.controls['firstname'].value,
      lastName: this.customerDetailsFormGroup.controls['lastname'].value,
      romanisedFirstName: this.customerDetailsFormGroup.controls['romanisedFirstName'].value,
      romanisedLastName: this.customerDetailsFormGroup.controls['romanisedLastName'].value,
      email: this.customerDetailsFormGroup.controls['email'].value,
      isoCountry: this.customerDetailsFormGroup.controls['country'].value?.[0] ?? undefined,
      address: this.customerDetailsFormGroup.controls['address'].value,
      postalCode: this.customerDetailsFormGroup.controls['postalCode'].value,
      birthDate: birthDateTimeless,
      gender: this.customerDetailsFormGroup.controls['gender'].value?.[0] ?? undefined,
      foreign: this.customerDetailsFormGroup.controls['foreign'].value === 'true',
      wantEmails: this.customerDetailsFormGroup.controls['wantEmails'].value,
      acceptedTerms: this.customerDetailsFormGroup.controls['acceptedTerms'].value,
      retailerId: this.customerDetailsFormGroup.controls['retailerId'].value?.[0],
      acceptedPrivacyPolicy: this.customerDetailsFormGroup.controls['acceptedPrivacyPolicy'].value
    };

    await firstValueFrom(this.customersService.apiCustomersCreatePost(payload));

    this.customerDetailsFormGroup.reset({
      firstname: '',
      lastname: '',
      romanisedFirstName: '',
      romanisedLastName: '',
      email: '',
      country: null,
      address: '',
      postalCode: '',
      birthDate: null,
      gender: null,
      foreign: 'false',
      wantEmails: false,
      acceptedTerms: false,
    });

    this.router.navigate(['/customers']);
  }
}
